/**
 * iter235 — Module Gardiennage pneus.
 *
 * Vue unique, mobile-first :
 *   - 4 compteurs cliquables (En stock / À facturer / Pas encore passés / Impayés)
 *   - filtres simples (recherche, type de pneus, stockés/sortis)
 *   - liste en cartes avec badges d'état et actions directes :
 *       Sortir & permuter · Facturer maintenant · Relancer (SMS/e-mail)
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  CircleDot, Search, Plus, Loader2, Receipt, Send, ArrowRightLeft, Car, X, User,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import VehiculePicker from "@/components/VehiculePicker";
import { useAuth } from "@/contexts/AuthContext";
import GardiennageTarifBtn from "@/components/gardiennage/GardiennageTarifBtn";
import { TYPE_LABEL, TYPE_CLASS, badgesOf, fmtChf } from "@/lib/gardiennage";

const COMPTEURS = [
  { key: "en_stock", label: "En stock", cls: "text-[#2d5f3f]" },
  { key: "a_facturer", label: "À facturer", cls: "text-red-600" },
  { key: "pas_passes", label: "Pas encore passés", cls: "text-orange-600" },
  { key: "impayes", label: "Impayés", cls: "text-blue-600" },
];

export default function Gardiennage() {
  const navigate = useNavigate();
  const { user } = useAuth();   // iter338 — tarif de gardiennage accessible ici aussi
  const [items, setItems] = useState([]);
  const [compteurs, setCompteurs] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typePneus, setTypePneus] = useState("tous");
  const [vue, setVue] = useState("stocke");   // stocke | tous | a_facturer | pas_passes | impayes
  const [newOpen, setNewOpen] = useState(false);
  const [sortie, setSortie] = useState({ open: false, g: null, creerNouveau: true, busy: false });
  const [relance, setRelance] = useState({ open: false, g: null, canal: "sms", message: "", busy: false });
  const [busyId, setBusyId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (q.trim()) params.q = q.trim();
      if (typePneus !== "tous") params.type_pneus = typePneus;
      if (vue === "stocke") params.statut = "stocke";
      if (vue === "a_facturer") params.facturation = "a_facturer";
      if (vue === "impayes") params.facturation = "impaye";
      if (vue === "pas_passes") params.a_monter = true;
      const { data } = await api.get("/gardiennages", { params });
      setItems(data.items || []);
      setCompteurs(data.compteurs || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [q, typePneus, vue]);

  useEffect(() => {
    const t = setTimeout(fetchList, 200);
    return () => clearTimeout(t);
  }, [fetchList]);

  const facturerMaintenant = async (g) => {
    if (!window.confirm(`Créer une facture de gardiennage pour ${g.plaque} — ${g.saison_label} (${fmtChf(g.tarif)}) ?`)) return;
    setBusyId(g.id);
    try {
      const { data: f } = await api.post("/factures", {
        client_id: g.client_id,
        vehicule_id: g.vehicule_id,
        titre: `Gardiennage pneus — ${g.saison_label}`,
        date_emission: new Date().toISOString().slice(0, 10),
        reference_libre: g.plaque || null,
        lignes: [{
          libelle: g.libelle_facture,
          quantite: 1,
          prix_unitaire_ht: g.tarif,
          tva_taux: 8.1,
          unite: "forfait",
          categorie_compta: "prestations",
        }],
      });
      await api.post(`/gardiennages/${g.id}/lier-facture`, { facture_id: f.id, facture_numero: f.numero });
      toast.success(`Facture ${f.numero} créée`);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de facturation");
    } finally {
      setBusyId(null);
    }
  };

  const doSortie = async () => {
    setSortie((s) => ({ ...s, busy: true }));
    try {
      await api.post(`/gardiennages/${sortie.g.id}/sortie`, { creer_nouveau: sortie.creerNouveau });
      toast.success(sortie.creerNouveau ? "Pneus montés · nouveau gardiennage créé" : "Pneus sortis du stock");
      setSortie({ open: false, g: null, creerNouveau: true, busy: false });
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      setSortie((s) => ({ ...s, busy: false }));
    }
  };

  const doRelance = async () => {
    setRelance((r) => ({ ...r, busy: true }));
    try {
      const { data } = await api.post(`/gardiennages/${relance.g.id}/relance`, {
        canal: relance.canal,
        message: relance.message || null,
      });
      toast.success(`Relance envoyée à ${data.to}`);
      setRelance({ open: false, g: null, canal: "sms", message: "", busy: false });
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
      setRelance((r) => ({ ...r, busy: false }));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="gardiennage-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2">
            <CircleDot className="w-7 h-7 text-[#52a675]" /> Gardiennage pneus
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Pneus stockés par véhicule · saison · facturation reprise automatiquement
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* iter339 — réglage du tarif réservé à l'admin */}
          {user?.role === "admin" && <GardiennageTarifBtn />}
          <Button onClick={() => setNewOpen(true)} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="gard-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nouveau gardiennage
          </Button>
        </div>
      </div>

      {/* Compteurs cliquables */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {COMPTEURS.map((c) => {
          const active = vue === (c.key === "en_stock" ? "stocke" : c.key);
          return (
            <button
              key={c.key}
              onClick={() => setVue(c.key === "en_stock" ? "stocke" : c.key)}
              className={`text-left p-3.5 rounded-lg border bg-white transition-all hover:shadow-sm ${active ? "border-[#52a675] ring-1 ring-[#52a675]/40" : "border-gray-200"}`}
              data-testid={`gard-compteur-${c.key}`}
            >
              <div className={`text-2xl font-bold ${c.cls}`}>{compteurs[c.key] ?? "—"}</div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">{c.label}</div>
            </button>
          );
        })}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Plaque, client, saison…"
            className="pl-9"
            data-testid="gard-search"
          />
        </div>
        <Select value={typePneus} onValueChange={setTypePneus}>
          <SelectTrigger className="w-[130px]" data-testid="gard-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tous">Tous pneus</SelectItem>
            <SelectItem value="hiver">Hiver</SelectItem>
            <SelectItem value="ete">Été</SelectItem>
          </SelectContent>
        </Select>
        <Select value={vue} onValueChange={setVue}>
          <SelectTrigger className="w-[170px]" data-testid="gard-filter-vue"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="stocke">En stock</SelectItem>
            <SelectItem value="a_facturer">À facturer</SelectItem>
            <SelectItem value="pas_passes">Pas encore passés</SelectItem>
            <SelectItem value="impayes">Impayés</SelectItem>
            <SelectItem value="tous">Tout l&apos;historique</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-gray-400 border-dashed" data-testid="gard-empty">
          Aucun gardiennage pour ce filtre.
        </Card>
      ) : (
        <div className="space-y-2.5" data-testid="gard-list">
          {items.map((g) => (
            <Card key={g.id} className="p-3.5 border-gray-200" data-testid={`gard-item-${g.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => navigate(`/clients/${g.client_id}?vehicule=${g.vehicule_id}`)}
                      className="font-bold text-gray-900 hover:text-[#2d5f3f] hover:underline font-mono"
                      data-testid={`gard-plaque-${g.id}`}
                    >
                      {g.plaque || "—"}
                    </button>
                    <Badge variant="outline" className={`text-[10px] ${TYPE_CLASS[g.type_pneus]}`}>
                      {g.quantite} pneus {TYPE_LABEL[g.type_pneus]?.toLowerCase()}
                    </Badge>
                    <span className="text-xs text-gray-500">{g.saison_label}</span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1 flex items-center gap-1.5 flex-wrap">
                    <User className="w-3 h-3 text-gray-400" />
                    <button
                      onClick={() => navigate(`/clients/${g.client_id}`)}
                      className="hover:underline"
                    >
                      {g.client_nom}
                    </button>
                    <span className="text-gray-300">·</span>
                    <span>Entré le {g.date_entree}</span>
                    <span className="text-gray-300">·</span>
                    <span className="font-semibold text-gray-800">{fmtChf(g.tarif)}</span>
                    {g.facturation_statut === "a_facturer" && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span className="text-red-600">facturable depuis le {g.facturable_des}</span>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {badgesOf(g).map((b) => (
                      <Badge key={b.key} variant="outline" className={`text-[10px] ${b.cls}`} data-testid={`gard-badge-${b.key}-${g.id}`}>
                        {b.label}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(g.statut || "stocke") === "stocke" && (
                    <Button
                      size="sm" variant="outline" className="h-8 text-xs"
                      onClick={() => setSortie({ open: true, g, creerNouveau: true, busy: false })}
                      data-testid={`gard-sortie-${g.id}`}
                    >
                      <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Changement
                    </Button>
                  )}
                  {g.a_monter && (
                    <Button
                      size="sm" variant="outline"
                      className="h-8 text-xs border-orange-300 text-orange-800 hover:bg-orange-50"
                      onClick={() => setRelance({ open: true, g, canal: "sms", message: "", busy: false })}
                      data-testid={`gard-relance-${g.id}`}
                    >
                      <Send className="w-3.5 h-3.5 mr-1" /> Relancer
                    </Button>
                  )}
                  {g.facturation_statut === "a_facturer" && !g.facture_id && (
                    <Button
                      size="sm"
                      className="h-8 text-xs bg-amber-600 hover:bg-amber-700"
                      disabled={busyId === g.id}
                      onClick={() => facturerMaintenant(g)}
                      data-testid={`gard-facturer-${g.id}`}
                    >
                      {busyId === g.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Receipt className="w-3.5 h-3.5 mr-1" /> Facturer</>}
                    </Button>
                  )}
                  {g.facture_id && (
                    <Button
                      size="sm" variant="ghost" className="h-8 text-xs"
                      onClick={() => navigate(`/facturation/${g.facture_id}`)}
                      data-testid={`gard-voir-facture-${g.id}`}
                    >
                      Voir la facture
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <NouveauGardiennageDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={fetchList} />

      {/* Changement de pneus */}
      <Dialog open={sortie.open} onOpenChange={(o) => !o && setSortie({ open: false, g: null, creerNouveau: true, busy: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Changement de pneus</DialogTitle>
            <DialogDescription>
              {sortie.g && `${sortie.g.quantite} pneus ${TYPE_LABEL[sortie.g.type_pneus]?.toLowerCase()} de ${sortie.g.plaque} sortent du stock et sont montés sur le véhicule.`}
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={sortie.creerNouveau}
              onChange={(e) => setSortie((s) => ({ ...s, creerNouveau: e.target.checked }))}
              className="mt-0.5 accent-[#52a675]"
              data-testid="gard-sortie-permuter"
            />
            <span>
              Les pneus démontés restent en gardiennage
              <span className="block text-xs text-gray-500">
                Crée le gardiennage de la saison suivante ({sortie.g && (sortie.g.type_pneus === "hiver" ? "pneus été" : "pneus hiver")}). L&apos;historique est conservé.
              </span>
            </span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSortie({ open: false, g: null, creerNouveau: true, busy: false })}>Annuler</Button>
            <Button onClick={doSortie} disabled={sortie.busy} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="gard-sortie-submit">
              {sortie.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Valider"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Relance client */}
      <Dialog open={relance.open} onOpenChange={(o) => !o && setRelance({ open: false, g: null, canal: "sms", message: "", busy: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Relancer le client</DialogTitle>
            <DialogDescription>
              {relance.g && `${relance.g.client_nom} · ${relance.g.plaque} — pneus toujours en stock.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={relance.canal} onValueChange={(v) => setRelance((r) => ({ ...r, canal: v }))}>
              <SelectTrigger data-testid="gard-relance-canal"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sms">SMS</SelectItem>
                <SelectItem value="email">E-mail</SelectItem>
              </SelectContent>
            </Select>
            <div>
              <Label className="text-xs">Message (vide = message par défaut)</Label>
              <Input
                value={relance.message}
                onChange={(e) => setRelance((r) => ({ ...r, message: e.target.value }))}
                placeholder="Vos pneus hiver sont chez nous, souhaitez-vous un RDV ?"
                data-testid="gard-relance-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRelance({ open: false, g: null, canal: "sms", message: "", busy: false })}>Annuler</Button>
            <Button onClick={doRelance} disabled={relance.busy} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="gard-relance-submit">
              {relance.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NouveauGardiennageDialog({ open, onClose, onCreated }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [client, setClient] = useState(null);
  const [vehicules, setVehicules] = useState([]);
  const [vehiculeId, setVehiculeId] = useState("");
  const [typePneus, setTypePneus] = useState("hiver");
  const [quantite, setQuantite] = useState(4);
  const [dateEntree, setDateEntree] = useState(new Date().toISOString().slice(0, 10));
  const [tarif, setTarif] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/gardiennages/config").then(({ data }) => setTarif(String(data.tarif_defaut ?? 60))).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || client || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        setResults(data.clients || []);
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, client, open]);

  useEffect(() => {
    if (!client?.id) { setVehicules([]); return; }
    api.get(`/clients/${client.id}`).then((r) => {
      const vs = r.data.vehicules || [];
      setVehicules(vs);
      if (vs.length === 1) setVehiculeId(vs[0].id);
    }).catch(() => {});
  }, [client?.id]);

  const reset = () => {
    setQ(""); setResults([]); setClient(null); setVehicules([]); setVehiculeId("");
    setTypePneus("hiver"); setQuantite(4); setBusy(false);
  };

  const submit = async () => {
    if (!vehiculeId) { toast.error("Sélectionne un véhicule"); return; }
    setBusy(true);
    try {
      await api.post("/gardiennages", {
        vehicule_id: vehiculeId,
        type_pneus: typePneus,
        quantite: parseInt(quantite, 10) || 4,
        date_entree: dateEntree,
        tarif: tarif !== "" ? parseFloat(tarif) : null,
      });
      toast.success("Gardiennage enregistré");
      reset();
      onClose();
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nouveau gardiennage</DialogTitle>
          <DialogDescription>
            Les pneus sont rattachés à la plaque. La saison et la date de facturation sont calculées automatiquement.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Client</Label>
            {client ? (
              <div className="flex items-center gap-2 p-2 border border-gray-200 rounded-md bg-gray-50">
                <User className="w-4 h-4 text-[#52a675]" />
                <div className="flex-1 text-sm font-semibold truncate">{client.nom}</div>
                <button onClick={() => { setClient(null); setVehiculeId(""); }} className="text-gray-400 hover:text-red-600" data-testid="gard-new-clear-client">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Nom, plaque, téléphone…" className="pl-9" autoFocus
                  data-testid="gard-new-client-search"
                />
                {results.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto">
                    {results.map((c) => {
                      const nom = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim();
                      return (
                        <button
                          key={c.id}
                          onClick={() => { setClient({ id: c.id, nom }); setQ(""); setResults([]); }}
                          className="w-full text-left px-3 py-2 hover:bg-[#f0f9f4] text-sm border-b border-gray-50 last:border-0"
                          data-testid={`gard-new-client-${c.id}`}
                        >
                          <div className="font-semibold">{nom}</div>
                          <div className="text-xs text-gray-500">{c.tel_principal} · {c.localite}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {client && (
            <div>
              <Label className="text-xs">Véhicule / plaque</Label>
              {vehicules.length === 0 ? (
                <div className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-md p-2">
                  Ce client n&apos;a aucun véhicule enregistré.
                </div>
              ) : (
                <VehiculePicker
                  vehicules={vehicules}
                  value={vehiculeId}
                  onChange={setVehiculeId}
                  testId="gard-new-vehicule"
                  showDetails
                  onVehiculeUpdated={(v) => setVehicules((prev) => prev.map((x) => (x.id === v.id ? { ...x, ...v } : x)))}
                />
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Pneus stockés</Label>
              <Select value={typePneus} onValueChange={setTypePneus}>
                <SelectTrigger data-testid="gard-new-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hiver">Hiver</SelectItem>
                  <SelectItem value="ete">Été</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Quantité</Label>
              <Input type="number" min="1" max="8" value={quantite} onChange={(e) => setQuantite(e.target.value)} data-testid="gard-new-quantite" />
            </div>
            <div>
              <Label className="text-xs">Date d&apos;entrée</Label>
              <Input type="date" value={dateEntree} onChange={(e) => setDateEntree(e.target.value)} data-testid="gard-new-date" />
            </div>
            <div>
              <Label className="text-xs">Tarif de la saison (CHF)</Label>
              <Input type="number" step="0.05" value={tarif} onChange={(e) => setTarif(e.target.value)} data-testid="gard-new-tarif" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Annuler</Button>
          <Button onClick={submit} disabled={busy || !vehiculeId} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="gard-new-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Car className="w-4 h-4 mr-1.5" /> Enregistrer</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
